import { myDate } from '@/commons/myDate';
import { useTimeAgo } from '@/commons/timeAgo';
import { getStatus } from '@/commons/getStatus';
import { useLocale } from '@/contexts/LocaleContext';
import { Badge, Button, Flex, Group, Text } from '@mantine/core';
import {
  IconArchive,
  IconCircleCheck,
  IconCircleX,
  IconFileText,
  IconFileImport,
  IconLock,
  IconMail,
  IconRosetteDiscountCheckFilled,
  IconSignature,
  IconList,
} from '@tabler/icons-react';
import { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFileActionsRedux } from '../hooks';
import { File as FileType } from '@/clients/pages/FolderPage/types';
import { FileActionsMenu } from './FileActionsMenu';
import {
  ConfirmVerifyModal,
  DeleteFileModal,
  ImportFieldsModal,
  PreviewFileModal,
  RenameFileModal,
  SendEmailModal,
} from './Modals';
import { OrganizePhotosInDigiStorageModal } from './Modals/OrganizePhotosInDigiStorage';
import { InfiniteListItemsModal } from './Modals/InfiniteListItemsModal';

interface FileItemProps {
  folder: any;
  file: FileType;
  availableFiles: FileType[];
  existingFileNames: string[];
}

const getStatusColorFromVariant = (variant: string): string => {
  switch (variant) {
    case 'positive':
      return 'green';
    case 'notice':
      return 'orange';
    case 'informative':
      return 'blue';
    case 'neutral':
      return 'gray';
    default:
      return 'gray';
  }
};

export const FileItem = ({
  folder,
  file,
  availableFiles,
  existingFileNames,
}: FileItemProps) => {
  const intl = useIntl();
  const timeAgo = useTimeAgo();
  const { locale } = useLocale();
  const [renameModalOpened, setRenameModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [previewModalOpened, setPreviewModalOpened] = useState(false);
  const [importFieldsModalOpened, setImportFieldsModalOpened] = useState(false);
  const [sendEmailModalOpened, setSendEmailModalOpened] = useState(false);
  const [verifyModalOpened, setVerifyModalOpened] = useState(false);
  const [
    organizePhotosInDigiStorageModalOpened,
    setOrganizePhotosInDigiStorageModalOpened,
  ] = useState(false);
  const [infiniteListItemsModalOpened, setInfiniteListItemsModalOpened] =
    useState(false);

  // Check if file has any signature field with value
  const hasSignatureWithValue = useMemo(
    () =>
      (file as any).fields?.some(
        (field: any) =>
          field.type === 'signature' &&
          field.value &&
          field.value.length > 0 &&
          field.value !== ''
      ) || false,
    [file]
  );

  // Check if file has any infinite-list field with values
  const hasInfiniteListWithValues = useMemo(
    () =>
      (file as any).fields?.some(
        (field: any) =>
          field.type === 'infinite-list' &&
          field.value &&
          Array.isArray(field.value) &&
          field.value.length > 0
      ) || false,
    [file]
  );

  // Check if there's at least one field with image values
  const hasFieldsWithValues = useMemo(() => {
    // Check direct imagePicker fields with values
    const hasDirectImagePickerWithValue = (file as any).fields?.some(
      (field: any) =>
        field.type === 'imagePicker' &&
        field.value &&
        (Array.isArray(field.value) ? field.value.length > 0 : true)
    );

    // Check infinite-list fields with imagePicker sub-fields that have values
    const hasInfiniteListWithImageValues = (file as any).fields?.some(
      (field: any) => {
        if (field.type === 'infinite-list') {
          const imagePickerInputs = (field.inputs || []).filter(
            (input: any) => input.type === 'imagePicker'
          );

          return imagePickerInputs.some((input: any) => {
            // Check if any entry in the infinite-list has images for this input
            return (field.value || []).some((entry: any) => {
              const entryImages = entry[input.name] || [];
              return Array.isArray(entryImages) && entryImages.length > 0;
            });
          });
        }
        return false;
      }
    );

    return hasDirectImagePickerWithValue || hasInfiniteListWithImageValues;
  }, [file]);

  const {
    handlePublish,
    handleLock,
    handleSign,
    handleVerify,
    handleDownload,
    handleSendEmail,
    handleImportFields,
    handleEditData,
    handleEditStructure,
    handleDuplicate,
    handleRename,
    handleDelete,
    handleOrganizePhotosInDigiStorage,
  } = useFileActionsRedux(folder, file);

  return (
    <>
      <RenameFileModal
        opened={renameModalOpened}
        onClose={() => setRenameModalOpened(false)}
        currentFileName={file.name}
        existingFileNames={existingFileNames}
        onRename={handleRename}
      />

      <DeleteFileModal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        fileName={file.name}
        onConfirm={handleDelete}
      />

      <PreviewFileModal
        opened={previewModalOpened}
        onClose={() => setPreviewModalOpened(false)}
        folderId={folder.id}
        fileId={file.id}
        fileDisplayName={file.name}
      />

      <ImportFieldsModal
        opened={importFieldsModalOpened}
        onClose={() => setImportFieldsModalOpened(false)}
        currentFile={file}
        availableFiles={availableFiles}
        onImport={handleImportFields}
      />

      <SendEmailModal
        opened={sendEmailModalOpened}
        onClose={() => setSendEmailModalOpened(false)}
        fileName={file.name}
        fileFields={(file as any).fields}
        onSend={handleSendEmail}
      />

      <ConfirmVerifyModal
        opened={verifyModalOpened}
        onClose={() => setVerifyModalOpened(false)}
        fileName={file.name}
        onConfirm={handleVerify}
      />

      <OrganizePhotosInDigiStorageModal
        opened={organizePhotosInDigiStorageModalOpened}
        onClose={() => setOrganizePhotosInDigiStorageModalOpened(false)}
        file={file}
        onOrganize={handleOrganizePhotosInDigiStorage}
      />

      <InfiniteListItemsModal
        opened={infiniteListItemsModalOpened}
        onClose={() => setInfiniteListItemsModalOpened(false)}
        file={file}
      />

      <Flex
        direction="row"
        gap="xs"
        align="center"
        justify="space-between"
        bdrs="xs"
      >
        <Flex direction="row" gap="xs" align="center">
          {file.isPublished ? (
            <IconCircleCheck size={16} color="green" />
          ) : (
            <IconCircleX size={16} color="red" />
          )}

          <IconFileText size={16} color="gray" />

          <Text size="sm">{file.name}.pdf</Text>

          <Badge variant="light" color="blue" size="sm">
            {intl.formatMessage({ id: file.type })}
          </Badge>

          <Badge
            variant="dot"
            color={getStatusColorFromVariant(getStatus(file.status).variant)}
            size="sm"
            title={intl.formatMessage({ id: getStatus(file.status).label })}
          >
            {intl.formatMessage({ id: getStatus(file.status).label })}
          </Badge>

          {file.isSigned && <IconSignature size={16} color="blue" />}

          {hasSignatureWithValue && <IconSignature size={16} color="green" />}

          {file.isVerified && (
            <IconRosetteDiscountCheckFilled size={16} color="green" />
          )}

          {file.isArchived && <IconArchive size={16} color="gray" />}

          {file.isLocked && <IconLock size={16} color="gray" />}

          {file.emailsSent > 0 && (
            <Group gap={2}>
              <Text size="xs">{file.emailsSent}x</Text>
              <IconMail size={16} color="gray" />
            </Group>
          )}

          <Text size="xs" c="dimmed">
            {intl.formatMessage({ id: 'updatedAt' })}{' '}
            {timeAgo.format(new Date(file.updatedAt))}
          </Text>
        </Flex>
        <Flex direction="row" gap="xs" align="center">
          <Text size="xs" c="dimmed">
            {intl.formatMessage({ id: 'added' })}{' '}
            {myDate(file.createdAt, locale)}
          </Text>

          {hasInfiniteListWithValues && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconList size={16} />}
              onClick={() => setInfiniteListItemsModalOpened(true)}
            >
              {intl.formatMessage({
                id: 'infinite-list',
                defaultMessage: 'List Items',
              })}
            </Button>
          )}

          {hasFieldsWithValues && (
            <Button
              size="xs"
              variant="light"
              color="blue"
              leftSection={<IconFileImport size={16} />}
              onClick={() => setOrganizePhotosInDigiStorageModalOpened(true)}
            >
              {intl.formatMessage({
                id: 'organizePhotosInDigiStorage',
              })}
            </Button>
          )}

          <FileActionsMenu
            file={file}
            onPublish={handlePublish}
            onLock={handleLock}
            onSign={handleSign}
            onVerify={() => setVerifyModalOpened(true)}
            onPreview={() => setPreviewModalOpened(true)}
            onDownload={handleDownload}
            onSendEmail={() => setSendEmailModalOpened(true)}
            onImportFields={() => setImportFieldsModalOpened(true)}
            onEditData={handleEditData}
            onEditStructure={handleEditStructure}
            onDuplicate={handleDuplicate}
            onRename={() => setRenameModalOpened(true)}
            onDelete={() => setDeleteModalOpened(true)}
          />
        </Flex>
      </Flex>
    </>
  );
};
